export interface CorePlanDisplayFeature {
  key: string;
  titleKey: string;
}

export interface PaidPlanDisplayFeature {
  key: string;
  type?: string;
  value?: unknown;
  limit?: unknown;
}

export const STANDARD_CORE_PLAN_DISPLAY_FEATURES = [
  {
    key: "arrivals",
    titleKey: "subscriptions.coreFeatures.arrivals",
  },
  {
    key: "in_house",
    titleKey: "subscriptions.coreFeatures.inHouse",
  },
  {
    key: "departures",
    titleKey: "subscriptions.coreFeatures.departures",
  },
  {
    key: "hotel_brand_settings",
    titleKey: "subscriptions.coreFeatures.hotelBrandSettings",
  },
  {
    key: "custom_pages",
    titleKey: "subscriptions.coreFeatures.customPages",
  },
  {
    key: "hotel_brands",
    titleKey: "subscriptions.coreFeatures.hotelBrands",
  },
  {
    key: "authorization",
    titleKey: "subscriptions.coreFeatures.authorization",
  },
  {
    key: "pms",
    titleKey: "subscriptions.coreFeatures.pms",
  },
  {
    key: "sso",
    titleKey: "subscriptions.coreFeatures.sso",
  },
  {
    key: "notifications",
    titleKey: "subscriptions.coreFeatures.notifications",
  },
  {
    key: "dashboard",
    titleKey: "subscriptions.coreFeatures.dashboard",
  },
  {
    key: "reservations",
    titleKey: "subscriptions.coreFeatures.reservations",
  },
  {
    key: "rooms",
    titleKey: "subscriptions.coreFeatures.rooms",
  },
  {
    key: "guests",
    titleKey: "subscriptions.coreFeatures.guests",
  },
  {
    key: "mobile_settings",
    titleKey: "subscriptions.coreFeatures.mobileSettings",
  },
] as const satisfies readonly CorePlanDisplayFeature[];

const STANDARD_CORE_PLAN_DISPLAY_FEATURE_BY_KEY = new Map<
  string,
  CorePlanDisplayFeature
>(STANDARD_CORE_PLAN_DISPLAY_FEATURES.map((feature) => [feature.key, feature]));

function isEnabledFeature(feature: PaidPlanDisplayFeature): boolean {
  const rawValue = feature.value ?? feature.limit;

  if (rawValue === undefined || rawValue === null) {
    return true;
  }

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue > 0;
  }

  if (typeof rawValue === "string") {
    const normalizedValue = rawValue.trim().toLowerCase();

    if (normalizedValue === "" || normalizedValue === "false") {
      return false;
    }

    if (feature.type === "NUMBER") {
      const numericValue = Number.parseFloat(normalizedValue);
      return !Number.isFinite(numericValue) || numericValue > 0;
    }
  }

  return true;
}

function getCoreDisplayFeature(feature: PaidPlanDisplayFeature) {
  const directCoreFeature = STANDARD_CORE_PLAN_DISPLAY_FEATURE_BY_KEY.get(
    feature.key,
  );

  if (directCoreFeature !== undefined) {
    return directCoreFeature;
  }

  if (feature.key === "rooms_limit") {
    return STANDARD_CORE_PLAN_DISPLAY_FEATURE_BY_KEY.get("rooms");
  }

  return undefined;
}

export type PlanDisplayFeature<TPaidFeature extends PaidPlanDisplayFeature> =
  | CorePlanDisplayFeature
  | TPaidFeature;

export function getPlanDisplayFeatures<
  TPaidFeature extends PaidPlanDisplayFeature,
>(
  paidFeatures: readonly TPaidFeature[],
  enableCoreFeatures: boolean,
): PlanDisplayFeature<TPaidFeature>[] {
  const enabledFeatures = paidFeatures.filter(isEnabledFeature);
  const selectedCoreFeatureKeys = new Set<string>();
  const explicitDisplayFeatures: PlanDisplayFeature<TPaidFeature>[] = [];

  for (const feature of enabledFeatures) {
    const coreFeature = getCoreDisplayFeature(feature);

    if (coreFeature !== undefined) {
      if (!selectedCoreFeatureKeys.has(coreFeature.key)) {
        selectedCoreFeatureKeys.add(coreFeature.key);
        explicitDisplayFeatures.push(coreFeature);
      }
      continue;
    }

    explicitDisplayFeatures.push(feature);
  }

  const displayFeatures: PlanDisplayFeature<TPaidFeature>[] =
    enableCoreFeatures && selectedCoreFeatureKeys.size === 0
      ? [...STANDARD_CORE_PLAN_DISPLAY_FEATURES, ...explicitDisplayFeatures]
      : explicitDisplayFeatures;

  return Array.from(
    new Map(displayFeatures.map((feature) => [feature.key, feature])).values(),
  );
}
